import React, { useState } from "react";
import axios from "axios";

const REASONS = [
  { id: "broken", label: "Doesn't load / broken" },
  { id: "spam", label: "Spam or misleading" },
  { id: "malicious", label: "Malicious or unsafe" },
  { id: "inappropriate", label: "Inappropriate content" },
  { id: "impersonation", label: "Impersonates someone" },
  { id: "other", label: "Something else" },
];

/**
 * Report a listing.
 *
 * Reports never hide anything on their own — they raise a flag for a human. The only
 * automated delisting in this system is the reachability sweep, which acts on an
 * objective signal (the app does not answer) rather than on somebody's opinion. See
 * backend/marketplace/schema/MarketplaceReport.js for why.
 *
 * `compact` renders the flag-only affordance used on cards; the full variant is for
 * the detail page.
 */
export default function ReportButton({ slug, name, compact = false }) {
  const [open, setOpen] = useState(false);
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const submit = async (reason) => {
    setBusy(true);
    setError("");
    try {
      await axios.post(`/api/marketplace/listings/${slug}/report`, { reason });
      setSent(true);
      setTimeout(() => setOpen(false), 1400);
    } catch (err) {
      // 429 is the one worth naming — it is the only failure a normal person can hit.
      setError(err?.response?.status === 429 ? "Too many reports. Try later." : "Couldn't send that.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <button
        type="button"
        onClick={(e) => { e.preventDefault(); e.stopPropagation(); setOpen(true); }}
        aria-label={`Report ${name}`}
        title="Report this listing"
        className={
          compact
            ? "flex h-6 w-6 items-center justify-center bg-black/70 text-[11px] text-white/60 backdrop-blur-sm transition-colors hover:bg-black/90 hover:text-red-300"
            : "text-xs text-silver-dark transition-colors hover:text-red-300"
        }
      >
        {compact ? "⚑" : "⚑ Report this listing"}
      </button>

      {open && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4"
          onClick={() => setOpen(false)}
        >
          <div
            className="w-full max-w-sm bg-[#0e0e12] p-5 ring-1 ring-white/15"
            onClick={(e) => e.stopPropagation()}
          >
            {sent ? (
              <p className="py-6 text-center text-sm text-lime-300">
                Thanks — a human will take a look.
              </p>
            ) : (
              <>
                <h3 className="text-sm font-bold text-silver-light">Report “{name}”</h3>
                <p className="mt-1 text-xs text-silver-dark">
                  What's wrong with it? This flags the listing for review — it doesn't
                  remove it automatically.
                </p>
                <div className="mt-4 flex flex-col gap-1">
                  {REASONS.map((r) => (
                    <button
                      key={r.id}
                      type="button"
                      disabled={busy}
                      onClick={() => submit(r.id)}
                      className="px-3 py-2 text-left text-xs text-silver-medium ring-1 ring-white/10 transition-colors hover:bg-white/5 hover:text-silver-light disabled:opacity-50"
                    >
                      {r.label}
                    </button>
                  ))}
                </div>
                {error && <p className="mt-3 text-xs text-red-300">{error}</p>}
                <button
                  type="button"
                  onClick={() => setOpen(false)}
                  className="mt-4 w-full py-2 text-xs text-silver-dark hover:text-silver-light"
                >
                  Cancel
                </button>
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
}
